import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Tabs,
  Tab,
  Icon,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";
import AppConfig from "appConfig";
import QRCode from "react-qr-code";

export default function LoyaltyPromotionPage() {
  usePageTitle("Promotion Management");

  const { ref } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const [refresh, setRefresh] = useState(false);

  // Promotion Data State
  const [promotion, setPromotion] = useState(null);
  const [products, setProducts] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [groups, setGroups] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [agentGroups, setAgentGroups] = useState([]);
  const [geoTerritories, setGeoTerritories] = useState([]);
  const [tab, setTab] = useState(0);

  // Promotion Dialog States
  const [promotionDialog, setPromotionDialog] = useState(false);
  const [promotionTitle, setPromotionTitle] = useState("");
  const [promotionDescription, setPromotionDescription] = useState("");

  // Product Dialog States
  const [productDialog, setProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [prodTitle, setProdTitle] = useState("");
  const [prodPrice, setProdPrice] = useState(0);
  const [prodPoints, setProdPoints] = useState(0);
  const [prodMinQty, setProdMinQty] = useState(1);
  const [prodMinVolume, setProdMinVolume] = useState(0);
  const [prodPerUnit, setProdPerUnit] = useState(false);
  const [prodMaxPoints, setProdMaxPoints] = useState(0);
  const [prodStatus, setProdStatus] = useState(true);

  // Reward Dialog States
  const [rewardDialog, setRewardDialog] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [rewTitle, setRewTitle] = useState("");
  const [rewType, setRewType] = useState("");
  const [rewValue, setRewValue] = useState("");

  // Group Dialog States
  const [groupDialog, setGroupDialog] = useState(false);
  const [groupRef, setGroupRef] = useState("");
  const [groupStatus, setGroupStatus] = useState(true);

  // Territory Dialog States
  const [territoryDialog, setTerritoryDialog] = useState(false);
  const [territoryRef, setTerritoryRef] = useState("");

  // Product Image Dialog States
  const [imageDialog, setImageDialog] = useState(false);
  const [imageProductRef, setImageProductRef] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");

  const [shareDialog, setShareDialog] = useState(false);
  const qrCodeRef = useRef(null);

  const showMessage = (msg, sev = "error") => {
    setSeverity(sev);
    setErrMsg(msg);
    setSnackbarOpen(true);
  };

  // --- Fetch Promotion Data ---
  useEffect(() => {
    const fetchPromotionData = async () => {
      setLoading(true);

      await axios
        .get(`/portal/tm/loyalty/promotions/${ref}/fetch.json`)
        .then((res) => {
          setLoading(false);

          if (res.data.status === "success") {
            const data = res.data.data;

            setPromotion(data.promotion);
            setProducts(data.products || []);
            setRewards(data.rewards || []);
            setGroups(data.groups || []);
            setTerritories(data.territories || []);
            setAgentGroups(data.agent_groups || []);
            setGeoTerritories(data.geo_territories || []);
          } else {
            showMessage(res.data.data.message);
          }
        })
        .catch(() => {
          setLoading(false);
          showMessage("Failed to load promotion details.");
        });
    };

    fetchPromotionData();
  }, [ref, refresh]);

  // --- Promotion Actions ---
  const handleOpenPromotionDialog = () => {
    setPromotionTitle(promotion?.title || "");
    setPromotionDescription(promotion?.description || "");
    setPromotionDialog(true);
  };

  const handleSavePromotion = async () => {
    if (!promotionTitle.trim()) {
      showMessage("Promotion title is required.");
      return;
    }

    setPromotionDialog(false);
    setLoading(true);

    try {
      const res = await axios.post("/portal/tm/loyalty/promotions/edit.json", {
        reference: ref,
        title: promotionTitle.trim(),
        description: promotionDescription,
      });

      if (res.data.status === "success") {
        showMessage(res.data.data.message || "Promotion updated successfully.", "success");
        setRefresh((v) => !v);
      } else {
        showMessage(res.data.data.message || "Failed to update promotion.");
      }
    } catch (error) {
      showMessage(error.response?.data?.data?.message || "Failed to update promotion.");
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePromotionStatus = async () => {
    setLoading(true);

    try {
      const res = await axios.get(`/portal/tm/loyalty/promotions/${ref}/status.json`);

      if (res.data.status === "success") {
        showMessage(res.data.data.message || "Promotion status updated.", "success");
        setRefresh((v) => !v);
      } else {
        showMessage(res.data.data.message || "Failed to update promotion status.");
      }
    } catch (error) {
      showMessage(error.response?.data?.data?.message || "Failed to update promotion status.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePromotion = async () => {
    if (
      !window.confirm(
        `Delete promotion "${promotion?.title || "this promotion"}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    setLoading(true);

    try {
      const res = await axios.get(`/portal/tm/loyalty/promotions/${ref}/delete.json`);

      if (res.data.status === "success") {
        showMessage(res.data.data.message || "Promotion deleted successfully.", "success");

        navigate("/trade-marketing/promotions/loyalty");
      } else {
        showMessage(res.data.data.message || "Failed to delete promotion.");
      }
    } catch (error) {
      showMessage(error.response?.data?.data?.message || "Failed to delete promotion.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyShareLink = async () => {
    if (!promotion?.share_link) {
      showMessage("Share link is not available.");
      return;
    }

    try {
      await navigator.clipboard.writeText(promotion.share_link);
      showMessage("Share link copied to clipboard.", "success");
    } catch (error) {
      showMessage("Unable to copy automatically. Select and copy the share link.");
    }
  };

  // --- Product Actions ---
  const handleOpenProductDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProdTitle(product.title);
      setProdPrice(product.price);
      setProdPoints(product.reward_points);
      setProdMinQty(product.min_qty);
      setProdMinVolume(product.min_volume);
      setProdPerUnit(product.per_unit);
      setProdMaxPoints(product.max_reward_points);
      setProdStatus(product.status);
    } else {
      setEditingProduct(null);
      setProdTitle("");
      setProdPrice(0);
      setProdPoints(0);
      setProdMinQty(1);
      setProdMinVolume(0);
      setProdPerUnit(false);
      setProdMaxPoints(0);
      setProdStatus(true);
    }

    setProductDialog(true);
  };

  const handleSaveProduct = async () => {
    setProductDialog(false);
    setLoading(true);

    const endpoint = editingProduct
      ? "/portal/tm/loyalty/promotions/products/edit.json"
      : "/portal/tm/loyalty/promotions/products/create.json";

    const payload = {
      promotion: ref,
      reference: editingProduct?.reference,
      title: prodTitle,
      price: prodPrice,
      reward_points: prodPoints,
      min_qty: prodMinQty,
      min_volume: prodMinVolume,
      per_unit: prodPerUnit,
      max_reward_points: prodMaxPoints,
      status: prodStatus,
    };

    await axios
      .post(endpoint, payload)
      .then((res) => {
        setLoading(false);

        if (res.data.status === "success") {
          showMessage(res.data.data.message, "success");
          setRefresh((v) => !v);
        } else {
          showMessage(res.data.data.message);
        }
      })
      .catch(() => {
        setLoading(false);
        showMessage("Operation failed.");
      });
  };

  const handleDeleteProduct = async (prodRef) => {
    if (!window.confirm("Delete this product?")) return;

    setLoading(true);

    await axios
      .get(`/portal/tm/loyalty/promotions/products/${prodRef}/delete.json`)
      .then((res) => {
        setLoading(false);

        if (res.data.status === "success") {
          showMessage(res.data.data.message, "success");
          setRefresh((v) => !v);
        } else {
          showMessage(res.data.data.message);
        }
      })
      .catch(() => {
        setLoading(false);
        showMessage("Failed to delete product.");
      });
  };

  // --- Product Image Actions ---
  useEffect(() => {
    if (!imagePreview) return undefined;

    return () => URL.revokeObjectURL(imagePreview);
  }, [imagePreview]);

  const handleOpenImageDialog = (productRef) => {
    setImageProductRef(productRef);
    setImageFile(null);
    setImagePreview("");
    setImageDialog(true);
  };

  const handleImageFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      showMessage("Please select an image file.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showMessage("Image size must not exceed 10 MB.");
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleUploadImage = async () => {
    if (!imageFile || !imageProductRef) {
      showMessage("Choose an image before uploading.");
      return;
    }

    setLoading(true);
    setImageDialog(false);

    const formData = new FormData();
    formData.append("product", imageProductRef);
    formData.append("image", imageFile);

    await axios
      .post("/portal/tm/loyalty/promotions/products/images/create.json", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((res) => {
        setLoading(false);

        if (res.data.status === "success") {
          setImageFile(null);
          setImagePreview("");
          setImageProductRef(null);

          showMessage("Image uploaded successfully.", "success");
          setRefresh((v) => !v);
        } else {
          showMessage(res.data.data.message);
        }
      })
      .catch(() => {
        setLoading(false);
        showMessage("Image upload failed.");
      });
  };

  const handleDeleteImage = async (imgRef) => {
    if (!window.confirm("Delete this product image?")) return;

    setLoading(true);

    await axios
      .get(`/portal/tm/loyalty/promotions/products/images/${imgRef}/delete.json`)
      .then((res) => {
        setLoading(false);

        if (res.data.status === "success") {
          showMessage("Image deleted.", "success");
          setRefresh((v) => !v);
        } else {
          showMessage(res.data.data.message);
        }
      })
      .catch(() => {
        setLoading(false);
        showMessage("Failed to delete image.");
      });
  };

  // --- Reward Actions ---
  const handleOpenRewardDialog = (reward = null) => {
    if (reward) {
      setEditingReward(reward);
      setRewTitle(reward.title);
      setRewType(reward.reward_type);
      setRewValue(reward.reward_value);
    } else {
      setEditingReward(null);
      setRewTitle("");
      setRewType("");
      setRewValue("");
    }

    setRewardDialog(true);
  };

  const handleSaveReward = async () => {
    setRewardDialog(false);
    setLoading(true);

    const endpoint = editingReward
      ? "/portal/tm/loyalty/promotions/rewards/edit.json"
      : "/portal/tm/loyalty/promotions/rewards/create.json";

    const payload = {
      promotion: ref,
      reference: editingReward?.reference,
      title: rewTitle,
      reward_type: rewType,
      reward_value: rewValue,
    };

    await axios
      .post(endpoint, payload)
      .then((res) => {
        setLoading(false);

        if (res.data.status === "success") {
          showMessage(res.data.data.message, "success");
          setRefresh((v) => !v);
        } else {
          showMessage(res.data.data.message);
        }
      })
      .catch(() => {
        setLoading(false);
        showMessage("Reward operation failed.");
      });
  };

  const handleDeleteReward = async (rewRef) => {
    if (!window.confirm("Delete this reward?")) return;

    setLoading(true);

    await axios
      .get(`/portal/tm/loyalty/promotions/rewards/${rewRef}/delete.json`)
      .then((res) => {
        setLoading(false);

        if (res.data.status === "success") {
          showMessage("Reward deleted.", "success");
          setRefresh((v) => !v);
        } else {
          showMessage(res.data.data.message);
        }
      })
      .catch(() => {
        setLoading(false);
        showMessage("Failed to delete reward.");
      });
  };

  // --- Group Actions ---
  const handleCreateGroup = async () => {
    if (!groupRef) {
      showMessage("Select an agent group.");
      return;
    }

    setGroupDialog(false);
    setLoading(true);

    await axios
      .post("/portal/tm/loyalty/promotions/groups/create.json", {
        promotion: ref,
        group: groupRef,
        status: groupStatus,
      })
      .then((res) => {
        setLoading(false);

        if (res.data.status === "success") {
          showMessage("Group assigned successfully.", "success");
          setGroupRef("");
          setRefresh((v) => !v);
        } else {
          showMessage(res.data.data.message);
        }
      })
      .catch(() => {
        setLoading(false);
        showMessage("Failed to assign group.");
      });
  };

  const handleDeleteGroup = async (groupRefId) => {
    if (!window.confirm("Unassign this agent group?")) return;

    setLoading(true);

    await axios
      .get(`/portal/tm/loyalty/promotions/groups/${groupRefId}/delete.json`)
      .then((res) => {
        setLoading(false);

        if (res.data.status === "success") {
          showMessage("Group unassigned.", "success");
          setRefresh((v) => !v);
        } else {
          showMessage(res.data.data.message);
        }
      })
      .catch(() => {
        setLoading(false);
        showMessage("Failed to unassign group.");
      });
  };

  // --- Territory Actions ---
  const handleCreateTerritory = async () => {
    if (!territoryRef) {
      showMessage("Select a territory.");
      return;
    }

    setTerritoryDialog(false);
    setLoading(true);

    await axios
      .post("/portal/tm/loyalty/promotions/territories/create.json", {
        promotion: ref,
        territory: territoryRef,
      })
      .then((res) => {
        setLoading(false);

        if (res.data.status === "success") {
          showMessage("Territory assigned.", "success");
          setTerritoryRef("");
          setRefresh((v) => !v);
        } else {
          showMessage(res.data.data.message);
        }
      })
      .catch(() => {
        setLoading(false);
        showMessage("Failed to assign territory.");
      });
  };

  const handleDeleteTerritory = async (terrRefId) => {
    if (!window.confirm("Unassign this territory?")) return;

    setLoading(true);

    await axios
      .get(`/portal/tm/loyalty/promotions/territories/${terrRefId}/delete.json`)
      .then((res) => {
        setLoading(false);

        if (res.data.status === "success") {
          showMessage("Territory unassigned.", "success");
          setRefresh((v) => !v);
        } else {
          showMessage(res.data.data.message);
        }
      })
      .catch(() => {
        setLoading(false);
        showMessage("Failed to unassign territory.");
      });
  };

  const handleDownloadQRCode = () => {
    const svg = qrCodeRef.current?.querySelector("svg");
    if (!svg) {
      showMessage("QR code is not available to download.");
      return;
    }

    const svgData = new XMLSerializer().serializeToString(svg);
    const svgBlob = new Blob([svgData], { type: "image/svg+xml;charset=utf-8" });
    const svgUrl = URL.createObjectURL(svgBlob);
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 600;
      canvas.height = 600;
      const context = canvas.getContext("2d");
      context.fillStyle = "#FFFFFF";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      const link = document.createElement("a");
      link.download = `loyalty-promo-${ref}-qr.png`;
      link.href = canvas.toDataURL("image/png");
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(svgUrl);
    };

    image.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      showMessage("Unable to generate QR code image.");
    };

    image.src = svgUrl;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      {/* --- Promotion Edit Dialog --- */}
      <Dialog
        open={promotionDialog}
        onClose={() => setPromotionDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Promotion</DialogTitle>

        <DialogContent>
          <MDBox mt={1}>
            <MDInput
              label="Promotion Title"
              fullWidth
              value={promotionTitle}
              onChange={(e) => setPromotionTitle(e.target.value)}
            />
          </MDBox>

          <MDBox mt={3}>
            <MDInput
              label="Description"
              fullWidth
              multiline
              minRows={3}
              value={promotionDescription}
              onChange={(e) => setPromotionDescription(e.target.value)}
            />
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton onClick={() => setPromotionDialog(false)} color="dark" variant="outlined">
            Cancel
          </MDButton>

          <MDButton onClick={handleSavePromotion} color="dark" variant="contained">
            Save Changes
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* --- Share QR Dialog --- */}
      <Dialog open={shareDialog} onClose={() => setShareDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Share Promotion</DialogTitle>

        <DialogContent>
          <MDBox mt={1} display="flex" flexDirection="column" alignItems="center" gap={2}>
            <MDTypography variant="body2" color="text" textAlign="center">
              Scan this QR code to open the public promotion page.
            </MDTypography>

            {promotion?.share_link && (
              <MDBox
                component="img"
                src={`https://api.qrserver.com/v1/create-qr-code/?size=280x280&margin=12&data=${encodeURIComponent(
                  promotion.share_link
                )}`}
                alt="Promotion QR code"
                sx={{
                  width: 280,
                  maxWidth: "100%",
                  height: "auto",
                  border: "1px solid #eaecf0",
                  borderRadius: "8px",
                  p: 1,
                  bgcolor: "#fff",
                }}
              />
            )}

            <MDInput
              label="Share Link"
              fullWidth
              value={promotion?.share_link || ""}
              InputProps={{ readOnly: true }}
            />
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap" }}>
          <MDButton onClick={() => setShareDialog(false)} color="dark" variant="outlined">
            Close
          </MDButton>

          <MDButton
            onClick={handleCopyShareLink}
            color="dark"
            variant="contained"
            startIcon={<Icon>content_copy</Icon>}
          >
            Copy Link
          </MDButton>

          <MDButton
            component="a"
            href={promotion?.share_link || "#"}
            target="_blank"
            rel="noreferrer"
            color="dark"
            variant="contained"
            startIcon={<Icon>open_in_new</Icon>}
            disabled={!promotion?.share_link}
          >
            Open Page
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* --- Product Dialog --- */}
      <Dialog open={productDialog} onClose={() => setProductDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editingProduct ? "Edit Product" : "Add Promotion Product"}</DialogTitle>

        <DialogContent>
          <MDBox mt={1}>
            <MDInput
              label="Product Title"
              fullWidth
              value={prodTitle}
              onChange={(e) => setProdTitle(e.target.value)}
            />
          </MDBox>

          <Grid container spacing={3} mt={1}>
            <Grid item xs={12}>
              <MDInput
                label="Price"
                type="number"
                fullWidth
                value={prodPrice}
                onChange={(e) => setProdPrice(e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <MDInput
                label="Reward Points"
                type="number"
                fullWidth
                value={prodPoints}
                onChange={(e) => setProdPoints(e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <MDInput
                label="Min Quantity"
                type="number"
                fullWidth
                value={prodMinQty}
                onChange={(e) => setProdMinQty(e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <MDInput
                label="Min Volume"
                type="number"
                fullWidth
                value={prodMinVolume}
                onChange={(e) => setProdMinVolume(e.target.value)}
              />
            </Grid>

            <Grid item xs={6}>
              <MDInput
                label="Max Reward Points"
                type="number"
                fullWidth
                value={prodMaxPoints}
                onChange={(e) => setProdMaxPoints(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={prodPerUnit}
                    onChange={(e) => setProdPerUnit(e.target.checked)}
                  />
                }
                label="Reward points awarded per product unit?"
              />
            </Grid>

            <Grid item xs={12} display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Switch checked={prodStatus} onChange={(e) => setProdStatus(e.target.checked)} />
                }
                label="Active Status"
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={() => setProductDialog(false)} color="dark" variant="outlined">
            Cancel
          </MDButton>

          <MDButton onClick={handleSaveProduct} color="dark" variant="contained">
            Save
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* --- Reward Dialog --- */}
      <Dialog open={rewardDialog} onClose={() => setRewardDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editingReward ? "Edit Reward" : "Add Promotion Reward"}</DialogTitle>

        <DialogContent>
          <MDBox mt={1}>
            <MDInput
              label="Reward Title"
              fullWidth
              value={rewTitle}
              onChange={(e) => setRewTitle(e.target.value)}
            />
          </MDBox>

          <MDBox mt={3}>
            <MDInput
              label="Reward Type"
              fullWidth
              value={rewType}
              onChange={(e) => setRewType(e.target.value)}
              select
              SelectProps={{
                style: {
                  padding: "12px 0px",
                },
              }}
            >
              <MenuItem key="AIRTIME" value="AIRTIME">
                AIRTIME
              </MenuItem>

              <MenuItem key="CASH" value="CASH">
                CASH
              </MenuItem>

              <MenuItem key="PRODUCT" value="PRODUCT">
                PRODUCT
              </MenuItem>
            </MDInput>
          </MDBox>

          <MDBox mt={3}>
            <MDInput
              label="Reward Value"
              fullWidth
              value={rewValue}
              onChange={(e) => setRewValue(e.target.value)}
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={() => setRewardDialog(false)} color="dark" variant="outlined">
            Cancel
          </MDButton>

          <MDButton onClick={handleSaveReward} color="dark" variant="contained">
            Save
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* --- Group Assignment Dialog --- */}
      <Dialog open={groupDialog} onClose={() => setGroupDialog(false)} fullWidth maxWidth="xs">
        <DialogTitle>Assign Group</DialogTitle>

        <DialogContent>
          <MDBox mt={1}>
            <FormControl fullWidth>
              <MDInput
                labelId="agent-group-label"
                value={groupRef}
                label="Agent Group"
                onChange={(e) => setGroupRef(e.target.value)}
                SelectProps={{
                  style: {
                    padding: "12px 0px",
                  },
                }}
                select
              >
                <MenuItem value="">
                  <em>Select an agent group</em>
                </MenuItem>

                {agentGroups.map((group) => (
                  <MenuItem key={group.reference} value={group.reference}>
                    {group.title}
                  </MenuItem>
                ))}
              </MDInput>
            </FormControl>
          </MDBox>

          <MDBox mt={2}>
            <FormControlLabel
              control={
                <Switch checked={groupStatus} onChange={(e) => setGroupStatus(e.target.checked)} />
              }
              label="Active"
            />
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={() => setGroupDialog(false)} color="dark" variant="outlined">
            Cancel
          </MDButton>

          <MDButton onClick={handleCreateGroup} color="dark" variant="contained">
            Assign
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* --- Territory Assignment Dialog --- */}
      <Dialog
        open={territoryDialog}
        onClose={() => setTerritoryDialog(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Assign Territory</DialogTitle>

        <DialogContent>
          <MDBox mt={1}>
            <FormControl fullWidth>
              <MDInput
                labelId="geo-territory-label"
                value={territoryRef}
                label="Territory"
                onChange={(e) => setTerritoryRef(e.target.value)}
                select
                SelectProps={{
                  style: {
                    padding: "12px 0px",
                  },
                }}
              >
                <MenuItem value="">
                  <em>Select a territory</em>
                </MenuItem>

                {geoTerritories.map((territory) => (
                  <MenuItem key={territory.reference} value={territory.reference}>
                    {territory.title}
                  </MenuItem>
                ))}
              </MDInput>
            </FormControl>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={() => setTerritoryDialog(false)} color="dark" variant="outlined">
            Cancel
          </MDButton>

          <MDButton onClick={handleCreateTerritory} color="dark" variant="contained">
            Assign
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* --- Product Image Upload Dialog --- */}
      <Dialog
        open={imageDialog}
        onClose={() => {
          setImageDialog(false);
          setImageFile(null);
          setImagePreview("");
          setImageProductRef(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload Product Image</DialogTitle>

        <DialogContent>
          <MDBox mt={1}>
            <MDTypography variant="body2" color="text" mb={2}>
              Choose a clear image of the product. JPG, PNG, or WEBP images up to 10 MB are
              recommended.
            </MDTypography>

            <MDBox
              component="label"
              htmlFor="product-image-input"
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                p: 3,
                border: "2px dashed",
                borderColor: imageFile ? "success.main" : "grey.400",
                borderRadius: 2,
                backgroundColor: imageFile ? "success.50" : "grey.50",
                cursor: "pointer",
                textAlign: "center",
                transition: "border-color 0.2s, background-color 0.2s",
                "&:hover": {
                  borderColor: "text.primary",
                  backgroundColor: "grey.100",
                },
              }}
            >
              <input
                id="product-image-input"
                type="file"
                accept="image/png,image/jpeg,image/webp,image/*"
                hidden
                onChange={handleImageFileChange}
              />

              {imagePreview ? (
                <MDBox
                  component="img"
                  src={imagePreview}
                  alt="Selected product preview"
                  sx={{
                    display: "block",
                    width: "100%",
                    maxHeight: 260,
                    objectFit: "contain",
                    borderRadius: 1,
                  }}
                />
              ) : (
                <>
                  <Icon sx={{ fontSize: 42, color: "text.secondary" }}>cloud_upload</Icon>

                  <MDTypography variant="button" fontWeight="medium">
                    Click to choose an image
                  </MDTypography>

                  <MDTypography variant="caption" color="text">
                    Select an image from your device
                  </MDTypography>
                </>
              )}
            </MDBox>

            {imageFile && (
              <MDBox
                mt={2}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
              >
                <MDBox sx={{ minWidth: 0 }}>
                  <MDTypography variant="button" display="block" noWrap>
                    {imageFile.name}
                  </MDTypography>

                  <MDTypography variant="caption" color="text">
                    {(imageFile.size / (1024 * 1024)).toFixed(2)} MB
                  </MDTypography>
                </MDBox>

                <MDButton
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => {
                    setImageFile(null);
                    setImagePreview("");
                  }}
                  startIcon={<Icon>close</Icon>}
                >
                  Remove
                </MDButton>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton
            onClick={() => {
              setImageDialog(false);
              setImageFile(null);
              setImagePreview("");
              setImageProductRef(null);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton
            onClick={handleUploadImage}
            color="dark"
            variant="contained"
            disabled={!imageFile || loading}
            startIcon={<Icon>cloud_upload</Icon>}
          >
            Upload Image
          </MDButton>
        </DialogActions>
      </Dialog>

      {promotion && (
        <MDBox mt={1} mb={5}>
          {/* Top Header & Quick Navigation Bar */}
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1.5}
            flexWrap="wrap"
            gap={1}
          >
            <MDBox>
              <MDButton
                variant="outlined"
                color="dark"
                size="small"
                onClick={() => navigate("/trade-marketing/promotions/loyalty")}
                startIcon={<Icon>west</Icon>}
              >
                Back
              </MDButton>
            </MDBox>

            <MDBox display="flex" gap={1} flexWrap="wrap">
              <MDButton
                variant="outlined"
                color="dark"
                size="small"
                onClick={() => navigate(`/tm/loyalty/promotions/${ref}/consumers`)}
                startIcon={<Icon>people</Icon>}
              >
                Consumers
              </MDButton>

              <MDButton
                variant="outlined"
                color="dark"
                size="small"
                onClick={() => navigate(`/tm/loyalty/promotions/${ref}/purchases`)}
                startIcon={<Icon>receipt</Icon>}
              >
                Purchases
              </MDButton>
            </MDBox>
          </MDBox>

          {/* Promotion Header */}
          <Grid container spacing={2.5} mb={4}>
            {/* Promotion Details */}
            <Grid item xs={12} lg={7}>
              <Card
                sx={{
                  p: { xs: 2, md: 3 },
                  height: "100%",
                  borderRadius: "12px",
                  boxShadow: "none",
                  border: "1px solid #e8eaf0",
                }}
              >
                <MDBox display="flex" flexDirection="column" height="100%" gap={2}>
                  <MDBox>
                    <MDBox display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                      <MDTypography variant="h4" fontWeight="bold">
                        {promotion.title}
                      </MDTypography>

                      <MDTypography
                        variant="button"
                        fontWeight="medium"
                        color={promotion.status ? "success" : "text"}
                      >
                        {promotion.status ? "● Active" : "○ Inactive"}
                      </MDTypography>
                    </MDBox>

                    <MDTypography variant="body2" color="text" mt={1}>
                      {promotion.description || "No description provided."}
                    </MDTypography>
                  </MDBox>

                  <MDBox display="flex" gap={1} flexWrap="wrap" mt="auto">
                    <MDButton
                      variant="outlined"
                      color="dark"
                      size="small"
                      onClick={handleOpenPromotionDialog}
                      startIcon={<Icon>edit</Icon>}
                    >
                      Edit
                    </MDButton>

                    <MDButton
                      variant="outlined"
                      color={promotion.status ? "warning" : "success"}
                      size="small"
                      onClick={handleTogglePromotionStatus}
                      startIcon={<Icon>{promotion.status ? "pause_circle" : "play_circle"}</Icon>}
                    >
                      {promotion.status ? "Deactivate" : "Activate"}
                    </MDButton>

                    <MDButton
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleDeletePromotion}
                      startIcon={<Icon>delete</Icon>}
                    >
                      Delete
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>

            {/* Share QR Code */}
            <Grid item xs={12} lg={5}>
              <Card
                sx={{
                  p: { xs: 2, md: 3 },
                  height: "100%",
                  borderRadius: "12px",
                  boxShadow: "none",
                  border: "1px solid #e8eaf0",
                }}
              >
                <MDBox
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  gap={1.8}
                  height="100%"
                >
                  <MDTypography variant="h6" fontWeight="bold" textAlign="center">
                    Share QR Code
                  </MDTypography>

                  {/* <MDTypography variant="body2" color="text" textAlign="center">
                    Scan the code to open the public promotion page.
                  </MDTypography> */}

                  {promotion.share_link ? (
                    <MDBox
                      ref={qrCodeRef}
                      sx={{
                        p: 1.5,
                        bgcolor: "#fff",
                        border: "1px solid #eaecf0",
                        borderRadius: "8px",
                        lineHeight: 0,
                        width: 220,
                        maxWidth: "100%",
                      }}
                    >
                      <QRCode
                        value={promotion.share_link}
                        size={196}
                        style={{
                          width: "100%",
                          height: "auto",
                          display: "block",
                        }}
                      />
                    </MDBox>
                  ) : (
                    <MDTypography variant="body2" color="text">
                      Share link is not available.
                    </MDTypography>
                  )}

                  {/* <MDInput
                    label="Share Link"
                    fullWidth
                    value={promotion.share_link || ""}
                    InputProps={{ readOnly: true }}
                  /> */}

                  <MDBox display="flex" gap={1} flexWrap="wrap" justifyContent="center" mt="auto">
                    <MDButton
                      onClick={handleCopyShareLink}
                      color="dark"
                      variant="contained"
                      size="small"
                      startIcon={<Icon>content_copy</Icon>}
                      disabled={!promotion.share_link}
                    >
                      Copy
                    </MDButton>

                    <MDButton
                      onClick={handleDownloadQRCode}
                      color="dark"
                      variant="outlined"
                      size="small"
                      startIcon={<Icon>download</Icon>}
                      disabled={!promotion.share_link}
                    >
                      Download
                    </MDButton>

                    <MDButton
                      component="a"
                      href={promotion.share_link || "#"}
                      target="_blank"
                      rel="noreferrer"
                      color="dark"
                      variant="outlined"
                      size="small"
                      startIcon={<Icon>open_in_new</Icon>}
                      disabled={!promotion.share_link}
                    >
                      Open
                    </MDButton>
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>

          {/* Management Tabs */}
          <Card>
            <MDBox p={1}>
              <Tabs
                value={tab}
                onChange={(e, val) => setTab(val)}
                sx={{
                  px: 2,
                  pt: 2,
                  borderBottom: "1px solid #f0f2f5",
                }}
              >
                <Tab label={`Products (${products.length})`} />
                <Tab label={`Rewards (${rewards.length})`} />
                <Tab label={`Agent Groups (${groups.length})`} />
                <Tab label={`Territories (${territories.length})`} />
              </Tabs>
            </MDBox>

            <MDBox p={3}>
              {/* TAB 0: PRODUCTS */}
              {tab === 0 && (
                <>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                    mb={2}
                  >
                    <MDTypography variant="h6">Promotion Products</MDTypography>

                    <MDButton
                      variant="contained"
                      color="dark"
                      size="small"
                      onClick={() => handleOpenProductDialog()}
                      startIcon={<Icon>add</Icon>}
                    >
                      Add Product
                    </MDButton>
                  </MDBox>

                  <DataTable
                    table={{
                      columns: [
                        { Header: "Title", accessor: "title" },
                        { Header: "Price", accessor: "price" },
                        { Header: "Points", accessor: "points" },
                        { Header: "Images", accessor: "images" },
                        { Header: "Status", accessor: "status" },
                        {
                          Header: "Actions",
                          accessor: "actions",
                          align: "right",
                        },
                      ],
                      rows: products.map((p) => ({
                        title: (
                          <MDBox>
                            <MDTypography variant="button" fontWeight="medium">
                              {p.title}
                            </MDTypography>

                            <MDTypography variant="caption" color="text" display="block">
                              Min Qty: {p.min_qty} | Volume: {p.min_volume}
                            </MDTypography>
                          </MDBox>
                        ),

                        price: p.price,
                        points: p.reward_points,

                        images: (
                          <MDBox
                            display="flex"
                            alignItems="center"
                            gap={1}
                            flexWrap="wrap"
                            sx={{ minWidth: 150 }}
                          >
                            {p.images && p.images.length > 0 ? (
                              p.images.map((img, index) => (
                                <MDBox
                                  key={img.reference}
                                  position="relative"
                                  sx={{
                                    width: 56,
                                    height: 56,
                                    flexShrink: 0,
                                    border: "1px solid",
                                    borderColor: "grey.300",
                                    borderRadius: 1.5,
                                    overflow: "hidden",
                                    backgroundColor: "grey.50",
                                    "&:hover .product-image-delete": {
                                      opacity: 1,
                                    },
                                  }}
                                >
                                  <Tooltip title={`View product image ${index + 1}`}>
                                    <MDBox
                                      component="a"
                                      href={`${AppConfig.api_media_url}${img.image}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      sx={{
                                        display: "block",
                                        width: "100%",
                                        height: "100%",
                                      }}
                                    >
                                      <MDBox
                                        component="img"
                                        src={`${AppConfig.api_media_url}${img.image}`}
                                        alt={`${p.title} image ${index + 1}`}
                                        sx={{
                                          display: "block",
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    </MDBox>
                                  </Tooltip>

                                  <Tooltip title="Delete image">
                                    <MDButton
                                      className="product-image-delete"
                                      size="small"
                                      color="error"
                                      iconOnly
                                      onClick={() => handleDeleteImage(img.reference)}
                                      sx={{
                                        position: "absolute",
                                        top: 2,
                                        right: 2,
                                        minWidth: 20,
                                        width: 20,
                                        height: 20,
                                        p: 0,
                                        opacity: 0,
                                        transition: "opacity 0.15s",
                                        backgroundColor: "white",
                                        boxShadow: 1,
                                        "&:hover": {
                                          backgroundColor: "grey.100",
                                        },
                                        "@media (hover: none)": {
                                          opacity: 1,
                                        },
                                      }}
                                    >
                                      <Icon
                                        sx={{
                                          fontSize: "13px !important",
                                        }}
                                      >
                                        close
                                      </Icon>
                                    </MDButton>
                                  </Tooltip>
                                </MDBox>
                              ))
                            ) : (
                              <MDTypography variant="caption" color="text">
                                No images
                              </MDTypography>
                            )}

                            <Tooltip title="Add product image">
                              <MDButton
                                size="small"
                                variant="outlined"
                                color="dark"
                                iconOnly
                                onClick={() => handleOpenImageDialog(p.reference)}
                              >
                                <Icon fontSize="small">add_a_photo</Icon>
                              </MDButton>
                            </Tooltip>
                          </MDBox>
                        ),

                        status: p.status ? "Active" : "Inactive",

                        actions: (
                          <MDBox display="flex" gap={1} justifyContent="flex-end">
                            <Tooltip title="Edit Product">
                              <MDButton
                                size="small"
                                variant="outlined"
                                color="dark"
                                iconOnly
                                onClick={() => handleOpenProductDialog(p)}
                              >
                                <Icon fontSize="small">edit</Icon>
                              </MDButton>
                            </Tooltip>

                            <Tooltip title="Delete Product">
                              <MDButton
                                size="small"
                                variant="outlined"
                                color="error"
                                iconOnly
                                onClick={() => handleDeleteProduct(p.reference)}
                              >
                                <Icon fontSize="small">delete</Icon>
                              </MDButton>
                            </Tooltip>
                          </MDBox>
                        ),
                      })),
                    }}
                    canSearch={false}
                    isSorted
                    entriesPerPage={false}
                    showTotalEntries
                    noEndBorder
                  />
                </>
              )}

              {/* TAB 1: REWARDS */}
              {tab === 1 && (
                <>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                    mb={2}
                  >
                    <MDTypography variant="h6">Promotion Rewards</MDTypography>

                    <MDButton
                      variant="contained"
                      color="dark"
                      size="small"
                      onClick={() => handleOpenRewardDialog()}
                      startIcon={<Icon>add</Icon>}
                    >
                      Add Reward
                    </MDButton>
                  </MDBox>

                  <DataTable
                    table={{
                      columns: [
                        { Header: "Title", accessor: "title" },
                        { Header: "Type", accessor: "type" },
                        { Header: "Value", accessor: "value" },
                        {
                          Header: "Actions",
                          accessor: "actions",
                          align: "right",
                        },
                      ],
                      rows: rewards.map((r) => ({
                        title: r.title,
                        type: r.reward_type,
                        value: r.reward_value,

                        actions: (
                          <MDBox display="flex" gap={1} justifyContent="flex-end">
                            <Tooltip title="Edit Reward">
                              <MDButton
                                size="small"
                                variant="outlined"
                                color="dark"
                                iconOnly
                                onClick={() => handleOpenRewardDialog(r)}
                              >
                                <Icon fontSize="small">edit</Icon>
                              </MDButton>
                            </Tooltip>

                            <Tooltip title="Delete Reward">
                              <MDButton
                                size="small"
                                variant="outlined"
                                color="error"
                                iconOnly
                                onClick={() => handleDeleteReward(r.reference)}
                              >
                                <Icon fontSize="small">delete</Icon>
                              </MDButton>
                            </Tooltip>
                          </MDBox>
                        ),
                      })),
                    }}
                    canSearch={false}
                    isSorted
                    entriesPerPage={false}
                    showTotalEntries
                    noEndBorder
                  />
                </>
              )}

              {/* TAB 2: GROUPS */}
              {tab === 2 && (
                <>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                    mb={2}
                  >
                    <MDTypography variant="h6">Assigned Groups</MDTypography>

                    <MDButton
                      variant="contained"
                      color="dark"
                      size="small"
                      onClick={() => setGroupDialog(true)}
                      startIcon={<Icon>add</Icon>}
                    >
                      Assign Group
                    </MDButton>
                  </MDBox>

                  <DataTable
                    table={{
                      columns: [
                        { Header: "Group Title", accessor: "title" },
                        { Header: "Status", accessor: "status" },
                        {
                          Header: "Actions",
                          accessor: "actions",
                          align: "right",
                        },
                      ],
                      rows: groups.map((g) => ({
                        title: g.title,
                        status: g.status ? "Active" : "Inactive",

                        actions: (
                          <Tooltip title="Unassign Group">
                            <MDButton
                              size="small"
                              variant="outlined"
                              color="error"
                              iconOnly
                              onClick={() => handleDeleteGroup(g.reference)}
                            >
                              <Icon fontSize="small">delete</Icon>
                            </MDButton>
                          </Tooltip>
                        ),
                      })),
                    }}
                    canSearch={false}
                    isSorted
                    entriesPerPage={false}
                    showTotalEntries
                    noEndBorder
                  />
                </>
              )}

              {/* TAB 3: TERRITORIES */}
              {tab === 3 && (
                <>
                  <MDBox
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={1}
                    mb={2}
                  >
                    <MDTypography variant="h6">Assigned Territories</MDTypography>

                    <MDButton
                      variant="contained"
                      color="dark"
                      size="small"
                      onClick={() => setTerritoryDialog(true)}
                      startIcon={<Icon>add</Icon>}
                    >
                      Assign Territory
                    </MDButton>
                  </MDBox>

                  <DataTable
                    table={{
                      columns: [
                        { Header: "Territory", accessor: "territory" },
                        {
                          Header: "Actions",
                          accessor: "actions",
                          align: "right",
                        },
                      ],
                      rows: territories.map((t) => ({
                        territory:
                          geoTerritories.find((territory) => territory.reference === t.territory)
                            ?.title || t.territory,

                        actions: (
                          <Tooltip title="Unassign Territory">
                            <MDButton
                              size="small"
                              variant="outlined"
                              color="error"
                              iconOnly
                              onClick={() => handleDeleteTerritory(t.reference)}
                            >
                              <Icon fontSize="small">delete</Icon>
                            </MDButton>
                          </Tooltip>
                        ),
                      })),
                    }}
                    canSearch={false}
                    isSorted
                    entriesPerPage={false}
                    showTotalEntries
                    noEndBorder
                  />
                </>
              )}
            </MDBox>
          </Card>
        </MDBox>
      )}

      <Footer />
    </DashboardLayout>
  );
}